/**
 * Displays detailed patient information with tabbed interface for uploads, profile, and gallery views.
 * Handles image uploads, simulation sharing, and navigation between patient overview and specific image sets.
 */
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
  useWindowDimensions,
  Image,
  ImageStyle,
} from "react-native";

import global from "../../../../styles/globalStyles";
import {
  TModal,
  TSimulationLinkAndPin,
  TSimulationRow,
  TClinicPatientType,
} from "../../../../utils/types";
import {
  TReturnGetImagesBatchBatchId,
  getImagesBatch,
  getImagesBatchBatchId,
  postTransformBatchShare,
} from "../../../../services/api";
import { SessionContext } from "../../../../contexts";
import { capitalizeString } from "../../../../utils/functions";
import { clientDomain } from "../../../../config";
import { sexes } from "../../../../utils/constant";
import { useImageUpload } from "../../../../utils/imageUpload";

import {
  InputPatientDetailPhone,
  InputPatientDetail,
  InputPatientDetailDropdown,
  PrimaryButton,
} from "../../../common/native/index";
import { PatientDetailSimulation } from "../../../uncommon/native/index";
import { ModalOpener } from "../../../uncommon/native/modals/index";
import { Upload } from "../../../../assets";
import {
  getPatientImages,
  TPatientInfo,
} from "../../../../services/api-clinic";
import {
  SideBySideGalleryView,
  SimulationGalleryView,
  SimpleGalleryView,
} from "../../../common/native/index";
import ImageUploadComponent from "../../../common/native/ImageUpload";
import VoiceNotesList from "../consults/voice-notes/VoiceNotesList";
import { NormalText } from "../../../common/native";
import VoiceRecorder from "../consults/VoiceRecorder";
import AudioUploader from "../consults/AudioUploader";

const PatientNotesSection: React.FC<{ patient: TPatientInfo }> = ({
  patient,
}) => {
  const session = useContext(SessionContext)?.session;
  // The patient's `uuid` is their ClinicOS patient UUID. Some callers also attach
  // `clinicOSUUID` directly; prefer that, then fall back to the uuid.
  const clinicOSUUID =
    (patient as unknown as { clinicOSUUID?: string | null }).clinicOSUUID ??
    patient.uuid ??
    null;
  const [refreshKey, setRefreshKey] = useState(0);

  const patientName = useMemo(() => {
    const name = `${patient.firstName || ""} ${patient.lastName || ""}`
      .trim()
      .replace(/\s+/g, " ");
    return name || patient.email || "Patient";
  }, [patient.firstName, patient.lastName, patient.email]);

  const voiceRecorderSession = useMemo(
    () =>
      session
        ? {
            userEmail: session.userEmail || undefined,
            clinicName: session.clinicName || undefined,
            accessToken: session.accessToken,
          }
        : undefined,
    [session],
  );

  const notePatient = useMemo(
    () => ({
      id: patient.uuid,
      name: patientName,
      consultationDate:
        patient.updatedAt || patient.createdAt || new Date().toISOString(),
      patientRecordId: patient.uuid,
      clinicOSUUID: clinicOSUUID ?? undefined,
    }),
    [
      patient.uuid,
      patient.updatedAt,
      patient.createdAt,
      patientName,
      clinicOSUUID,
    ],
  );

  const recorderPatient = useMemo(
    () => ({
      id: patient.uuid,
      name: patientName,
      patientRecordId: patient.uuid,
      email: patient.email || undefined,
      clinicOSUUID: clinicOSUUID ?? undefined,
    }),
    [patient.uuid, patientName, patient.email, clinicOSUUID],
  );

  const handleVoiceNoteSaved = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  if (!session) return null;

  if (!clinicOSUUID) {
    return (
      <View style={styles.notesStatusContainer}>
        <Text style={[global.text, styles.notesStatusText]}>
          This patient isn&apos;t linked to a ClinicOS record, so notes are
          unavailable.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.voiceNotesContainer}>
      <View style={styles.voiceNotesActions}>
        <VoiceRecorder
          patient={recorderPatient}
          clinicId={session.clinicId || ""}
          onSaveRecording={handleVoiceNoteSaved}
          session={voiceRecorderSession}
          tipsEnabled={false}
        />
        <AudioUploader
          patient={recorderPatient}
          onUploadComplete={handleVoiceNoteSaved}
        />
      </View>
      <VoiceNotesList
        patient={notePatient}
        refreshKey={refreshKey}
        tipsEnabled={false}
      />
    </View>
  );
};

type PatientDetailProps = {
  currentPatientDetail: TPatientInfo;
  setPatientDetailToNull: () => void;
  unlockedFreeLeadUuid?: string | null;
  onStartConsult?: (patient: TPatientInfo) => void;
};

const PatientDetail: React.FC<PatientDetailProps> = ({
  currentPatientDetail,
  setPatientDetailToNull,
  unlockedFreeLeadUuid = null,
  onStartConsult,
}) => {
  const session = useContext(SessionContext)?.session;
  const { width } = useWindowDimensions();
  const isMobile = width < 644;
  const isFreePlan = session?.planType === "FREE";
  const isUnlockedFreeLead =
    currentPatientDetail.type === TClinicPatientType.LEAD &&
    currentPatientDetail.uuid === unlockedFreeLeadUuid;
  const shouldMaskLeadDetails =
    isFreePlan &&
    currentPatientDetail.type === TClinicPatientType.LEAD &&
    !isUnlockedFreeLead;
  const excludedUploadTypes = useMemo(
    () => ["TransformImageForForm", "TransformImageForUser"],
    [],
  );

  const [visible, setVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<TModal>("");

  const [simulationRows, setSimulationRows] = useState<TSimulationRow[]>([]);
  const [isLoadingSimulationRows, setIsLoadingSimulationRows] =
    useState<boolean>(true);

  const [isShowSimulationImages, setIsShowSimulationImages] =
    useState<boolean>(false);

  const [simulationLinkAndPin, setSimulationLinkAndPin] =
    useState<TSimulationLinkAndPin>({ simulationLink: "", pin: "" });

  const [currentPatientImageData, setCurrentPatientImageData] = useState<
    TReturnGetImagesBatchBatchId[]
  >([]);

  const patientDisplayName = useMemo(() => {
    const name = `${currentPatientDetail.firstName || ""} ${
      currentPatientDetail.lastName || ""
    }`
      .trim()
      .replace(/\s+/g, " ");
    return name || currentPatientDetail.email || "Patient";
  }, [
    currentPatientDetail.firstName,
    currentPatientDetail.lastName,
    currentPatientDetail.email,
  ]);

  const patientAvatarInitial = useMemo(
    () => (patientDisplayName.trim().charAt(0) || "?").toUpperCase(),
    [patientDisplayName],
  );

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchProfileImage = async () => {
      if (!session) return;

      try {
        const [headFrontImages, fallbackImages] = await Promise.all([
          getPatientImages(session, currentPatientDetail.uuid, {
            viewTypes: ["HEAD_FRONT"],
            uploadTypes: ["PatientImageForUser"],
          }),
          getPatientImages(session, currentPatientDetail.uuid, {
            viewTypes: ["HEAD_FRONT"],
          }),
        ]);

        const images =
          headFrontImages.length > 0 ? headFrontImages : fallbackImages;
        const image = images[0];
        const thumbnailUrl = image?.thumbnail_presigned_urls?.[0] ?? null;
        const fullUrl = image?.presigned_urls?.[0] ?? null;
        const selectedUrl = thumbnailUrl || fullUrl;

        if (!cancelled) {
          setProfileImageUrl(selectedUrl || null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching patient profile image:", error);
          setProfileImageUrl(null);
        }
      }
    };

    fetchProfileImage();

    return () => {
      cancelled = true;
    };
  }, [session, currentPatientDetail.uuid]);

  const resolvedProfileImageUrl = useMemo(() => {
    const runtimeProfileImage =
      (currentPatientDetail as { profileImageUrl?: string | null })
        ?.profileImageUrl ?? null;
    return profileImageUrl || runtimeProfileImage;
  }, [currentPatientDetail, profileImageUrl]);

  const patientDetailFields = (
    <>
      <InputPatientDetail
        name={"First name"}
        value={currentPatientDetail.firstName || "-"}
      />
      <InputPatientDetail
        name={"Last name"}
        value={currentPatientDetail.lastName || "-"}
      />
      <InputPatientDetail
        name={"Email"}
        value={
          shouldMaskLeadDetails ? "***" : currentPatientDetail.email || "-"
        }
      />
      {shouldMaskLeadDetails ? (
        <InputPatientDetail name={"Phone"} value={"***"} />
      ) : (
        <InputPatientDetailPhone value={currentPatientDetail.phone || "+1"} />
      )}
      <View style={{ zIndex: 1 }}>
        <InputPatientDetailDropdown
          name="Sex"
          value={[currentPatientDetail.sex || "-"]}
          selections={sexes}
        />
      </View>
    </>
  );

  // Use the image upload hook
  const {
    state: uploadState,
    manager: uploadManager,
    uploadImages,
  } = useImageUpload();

  const handleSelectedRow = useCallback(
    (batchId: string) => {
      const fetchBatchImages = async () => {
        if (session) {
          try {
            const patientImageDataRes: TReturnGetImagesBatchBatchId[] =
              await getImagesBatchBatchId(
                session,
                currentPatientDetail.uuid,
                batchId,
              );

            patientImageDataRes.sort((a, b) => {
              const viewTypeOrder = {
                TORSO_FRONT: 1,
                TORSO_LEFT_45: 2,
                TORSO_LEFT: 3,
                TORSO_RIGHT_45: 4,
                TORSO_RIGHT: 5,
                BODY_FRONT: 7,
                BODY_BACK: 8,
                BODY_LEFT: 9,
                BODY_RIGHT: 10,
                HEAD_FRONT: 11,
                HEAD_LEFT_45: 12,
                HEAD_LEFT: 13,
                HEAD_RIGHT_45: 14,
                HEAD_RIGHT: 15,
              };

              // @ts-ignore
              const viewTypeA = viewTypeOrder[a.viewType] || Infinity;
              // @ts-ignore
              const viewTypeB = viewTypeOrder[b.viewType] || Infinity;

              return viewTypeA - viewTypeB;
            });

            setCurrentPatientImageData(patientImageDataRes);
          } catch (error) {
            console.error("Error fetching clinic info:", error);
          }
        }
      };

      const fetchSimulationLink = async () => {
        if (session) {
          try {
            const postTransformBatchShareRes = await postTransformBatchShare(
              session,
              currentPatientDetail.uuid,
              batchId,
            );
            const simulataionLink = `${clientDomain}simulation/${postTransformBatchShareRes.shareId}`;
            const pin = postTransformBatchShareRes.pin;
            setSimulationLinkAndPin({
              simulationLink: simulataionLink,
              pin: pin,
            });
          } catch (error) {
            console.error("Error fetching clinic info:", error);
          }
        }
      };
      fetchBatchImages();
      fetchSimulationLink();
      setIsShowSimulationImages(true);
    },
    [session, currentPatientDetail.uuid],
  );

  const fetchImageData = useCallback(async () => {
    if (session) {
      setIsLoadingSimulationRows(true);
      try {
        const simulationRowsRes = await getImagesBatch(
          session,
          currentPatientDetail.uuid,
        );
        const sortingFunction = (a: TSimulationRow, b: TSimulationRow) => {
          const valueA = a["updated_at" as keyof TSimulationRow];
          const valueB = b["updated_at" as keyof TSimulationRow];
          // @ts-ignore
          const dateA = valueA ? new Date(valueA) : null;
          // @ts-ignore
          const dateB = valueB ? new Date(valueB) : null;

          if (dateA && dateB) return dateB.getTime() - dateA.getTime();
          else if (dateA) return 1;
          else if (dateB) return -1;
          return 0;
        };
        const sorted = [...simulationRowsRes].sort(sortingFunction);
        setSimulationRows(sorted);
      } catch (error) {
        console.error("Error fetching patient image batches:", error);
      } finally {
        setIsLoadingSimulationRows(false);
      }
    }
  }, [session, currentPatientDetail.uuid]);

  // Handle upload with session and patient info
  const handleUpload = useCallback(async () => {
    if (!session) return;

    await uploadImages(session, currentPatientDetail.uuid, () => {
      // On success, refresh simulation data
      fetchImageData();
    });
  }, [session, currentPatientDetail.uuid, uploadImages, fetchImageData]);

  useEffect(() => {
    fetchImageData();
  }, [fetchImageData]);

  return (
    <View style={styles.container}>
      <ModalOpener
        visible={visible}
        modalType={modalType}
        onClose={() => {
          fetchImageData();
          setVisible(false);
        }}
        currentPatientUuid={currentPatientDetail.uuid}
        simulationLinkAndPin={simulationLinkAndPin}
        patientType={TClinicPatientType.PATIENT}
      />
      <View style={styles.headerContainer}>
        <View style={styles.headerTitleRow}>
          <Pressable onPress={setPatientDetailToNull}>
            <Text
              style={[global.text, global.fontSize24, global.fontWeight500]}
            >
              Patient /
            </Text>
          </Pressable>
          {isShowSimulationImages && (
            <>
              <Pressable
                onPress={() => setIsShowSimulationImages(false)}
                style={styles.patientBreadcrumb}
              >
                <Text
                  style={[global.text, global.fontSize24, global.fontWeight500]}
                >
                  {patientDisplayName || "-"} /
                </Text>
              </Pressable>
              <Text
                style={[global.text, global.fontSize24, global.fontWeight700]}
              >
                Images
              </Text>
            </>
          )}
          {!isShowSimulationImages && (
            <View style={styles.patientNameRow}>
              <Text
                style={[global.text, global.fontSize24, global.fontWeight700]}
              >
                {patientDisplayName || "-"}
              </Text>
              {currentPatientDetail.type === TClinicPatientType.LEAD && (
                <View style={styles.leadBadge}>
                  <Text style={styles.leadBadgeText}>Lead</Text>
                </View>
              )}
            </View>
          )}
        </View>
        <View style={styles.headerActions}>
          {isShowSimulationImages ? (
            <PrimaryButton
              text={isMobile ? "" : "Share the Simulation"}
              roundBorderRadius
              icon={<Upload />}
              onPress={() => {
                setVisible(true);
                setModalType("ShareReportPdf");
              }}
            />
          ) : null}
          {!isShowSimulationImages &&
          !shouldMaskLeadDetails &&
          onStartConsult ? (
            <PrimaryButton
              text="Start Consult"
              onPress={() => onStartConsult?.(currentPatientDetail)}
            />
          ) : null}
        </View>
      </View>

      {isShowSimulationImages && (
        <PatientDetailSimulation patientImageData={currentPatientImageData} />
      )}

      {!isShowSimulationImages && (
        <ScrollView contentContainerStyle={styles.detailScrollContainer}>
          <View
            style={[
              styles.sectionCard,
              styles.profileSection,
              !isMobile && styles.profileSectionWide,
            ]}
          >
            <View style={styles.profileHeaderRow}>
              <NormalText text="Patient Details" style={styles.sectionTitle} />
              {resolvedProfileImageUrl ? (
                <View
                  style={[
                    styles.avatarContainer,
                    isMobile
                      ? styles.avatarContainerSmall
                      : styles.avatarContainerLarge,
                  ]}
                >
                  <Image
                    source={{ uri: resolvedProfileImageUrl }}
                    style={[
                      styles.avatarImage as ImageStyle,
                      shouldMaskLeadDetails &&
                        (styles.avatarImageMasked as ImageStyle),
                      isMobile
                        ? (styles.avatarImageSmall as ImageStyle)
                        : (styles.avatarImageLarge as ImageStyle),
                    ]}
                  />
                  {shouldMaskLeadDetails ? (
                    <View
                      style={[
                        styles.avatarMaskOverlay,
                        isMobile
                          ? styles.avatarMaskOverlaySmall
                          : styles.avatarMaskOverlayLarge,
                      ]}
                    >
                      <Text style={styles.avatarMaskText}>***</Text>
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
            {patientDetailFields}
          </View>

          <View style={styles.sectionCard}>
            <NormalText text="Photos" style={styles.sectionTitle} />
            {shouldMaskLeadDetails ? (
              <View style={styles.lockedMediaContainer}>
                <Text style={styles.lockedMediaMask}>***</Text>
                <NormalText
                  text="Upgrade to unlock lead photos"
                  style={styles.lockedMediaText}
                />
              </View>
            ) : (
              <SimpleGalleryView
                currentPatientDetail={currentPatientDetail}
                availableWidth={width - 280}
                excludedUploadTypes={excludedUploadTypes}
              />
            )}
          </View>

          <View style={styles.sectionCard}>
            <NormalText text="Simulations" style={styles.sectionTitle} />
            <SimulationGalleryView patientUuid={currentPatientDetail.uuid} />
          </View>

          <View style={styles.sectionCard}>
            <NormalText
              text="Side-by-Side Images"
              style={styles.sectionTitle}
            />
            <SideBySideGalleryView
              patientUuid={currentPatientDetail.uuid}
              availableWidth={width - 280}
            />
          </View>

          <View style={styles.sectionCard}>
            <NormalText text="Patient Uploads" style={styles.sectionTitle} />
            {shouldMaskLeadDetails ? (
              <View style={styles.lockedMediaContainer}>
                <Text style={styles.lockedMediaMask}>***</Text>
                <NormalText
                  text="Upgrade to unlock patient uploads"
                  style={styles.lockedMediaText}
                />
              </View>
            ) : (
              <>
                {isLoadingSimulationRows && simulationRows.length === 0 ? (
                  <View style={styles.uploadsLoading}>
                    <ActivityIndicator size="small" color="#00B7F4" />
                  </View>
                ) : simulationRows.length === 0 ? (
                  <NormalText
                    text="No photos uploaded."
                    style={styles.sectionEmptyText}
                  />
                ) : (
                  <View style={styles.uploadsList}>
                    {simulationRows.map((item, index) => {
                      const dateAdded = item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : "-";
                      const procedures = item.procedure_types?.length
                        ? item.procedure_types
                            .map((item2) => capitalizeString(item2))
                            .join(", ")
                        : "-";
                      const hasImages = Boolean(item.id);

                      return (
                        <View key={item.id || index} style={styles.uploadRow}>
                          <Text style={[global.text, styles.uploadBullet]}>
                            •
                          </Text>
                          <Text style={[global.text, styles.uploadText]}>
                            {dateAdded}, {procedures},
                          </Text>
                          <Pressable
                            onPress={() =>
                              hasImages ? handleSelectedRow(item.id) : null
                            }
                            disabled={!hasImages}
                          >
                            <Text
                              style={[
                                global.text,
                                styles.uploadLink,
                                !hasImages && styles.uploadLinkDisabled,
                              ]}
                            >
                              View images
                            </Text>
                          </Pressable>
                        </View>
                      );
                    })}
                  </View>
                )}
              </>
            )}
          </View>

          <View style={styles.sectionCard}>
            <ImageUploadComponent
              uploadState={uploadState}
              uploadManager={uploadManager}
              onUpload={handleUpload}
            />
          </View>

          <View style={styles.sectionCard}>
            <NormalText text="Voice Notes" style={styles.sectionTitle} />
            <PatientNotesSection patient={currentPatientDetail} />
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 3,
    minWidth: 0,
    flexShrink: 1,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  patientBreadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  patientNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  leadBadge: {
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  leadBadgeText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#1E40AF",
  },
  profileHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  avatarContainer: {
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#E2E8F0",
  },
  avatarContainerSmall: {
    width: 64,
    height: 64,
    borderRadius: 12,
  },
  avatarContainerLarge: {
    width: 72,
    height: 72,
    borderRadius: 16,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  avatarImageMasked: {
    opacity: 0.01,
  },
  avatarImageSmall: {
    borderRadius: 12,
  },
  avatarImageLarge: {
    borderRadius: 16,
  },
  avatarMaskOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 2,
    backgroundColor: "#F8FAFC",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarMaskOverlaySmall: {
    borderRadius: 12,
  },
  avatarMaskOverlayLarge: {
    borderRadius: 16,
  },
  avatarMaskText: {
    fontFamily: "Urbanist700",
    fontSize: 16,
    color: "#334155",
  },
  detailScrollContainer: {
    paddingBottom: 32,
    gap: 20,
  },
  sectionCard: {
    gap: 12,
    width: "100%",
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
  },
  profileSection: {
    maxWidth: 422,
    alignSelf: "flex-start",
    width: "auto",
  },
  profileSectionWide: {
    width: "auto",
  },
  sectionTitle: {
    fontSize: 18,
    color: "#0F172A",
  },
  sectionEmptyText: {
    color: "#64748B",
  },
  lockedMediaContainer: {
    minHeight: 160,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#CBD5E1",
    backgroundColor: "#F1F5F9",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  lockedMediaMask: {
    fontFamily: "Urbanist700",
    fontSize: 24,
    color: "#334155",
    letterSpacing: 1,
  },
  lockedMediaText: {
    color: "#64748B",
  },
  uploadsList: {
    gap: 10,
  },
  uploadsLoading: {
    paddingVertical: 8,
    alignItems: "flex-start",
  },
  uploadRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  uploadBullet: {
    marginRight: 2,
  },
  uploadText: {
    color: "#0F172A",
  },
  uploadLink: {
    color: "#2563EB",
  },
  uploadLinkDisabled: {
    color: "#94A3B8",
  },
  patientListHeader: {
    // @ts-ignore comment
    display: "grid",
    gridTemplateColumns: "100px 220px 130px",
    borderBottomColor: "#DDDEE0",
    borderBottomWidth: 1,
    paddingVertical: 20,
  },
  patientListRow: {
    // @ts-ignore comment
    display: "grid",
    gridTemplateColumns: "100px 220px 130px",
    paddingVertical: 6,
    marginVertical: 10,
  },
  notesStatusContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 160,
  },
  notesStatusText: {
    marginTop: 12,
    color: "#565970",
    textAlign: "center",
  },
  voiceNotesContainer: {
    gap: 16,
  },
  voiceNotesActions: {
    gap: 12,
  },
});

export default PatientDetail;
